import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLInputElement

// Dětská sekce (admin): samostatné formuláře, nesdílené s dospělými (ScoreEntryTable/TrainingForm/
// TournamentForm) - menší riziko rozbití stávajícího kódu pro dospělé. Vlastní vnitřní tab bar:
// Děti (soupiska) / Tréninky / Liga (kola s pořadím) / Turnaje (pořadí + parťák/mód poznámka).

@Composable
fun ChildrenAdminSection(token: String, member: SgMemberDto?) {
    var children by remember { mutableStateOf<List<SgChildDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var reloadKey by remember { mutableStateOf(0) }
    var subTab by remember { mutableStateOf("roster") }

    LaunchedEffect(reloadKey) {
        try {
            children = Api.fetchChildrenAdmin(token)
        } catch (e: Throwable) {
            error = e.apiMessage()
        } finally {
            loading = false
        }
    }

    Div(attrs = { classes("tab-bar") }) {
        Button(attrs = { classes(*(listOfNotNull("small-btn", "small-btn-on".takeIf { subTab == "roster" }).toTypedArray())); onClick { subTab = "roster" } }) { Text("Děti") }
        Button(attrs = { classes(*(listOfNotNull("small-btn", "small-btn-on".takeIf { subTab == "training" }).toTypedArray())); onClick { subTab = "training" } }) { Text("Tréninky") }
        Button(attrs = { classes(*(listOfNotNull("small-btn", "small-btn-on".takeIf { subTab == "round" }).toTypedArray())); onClick { subTab = "round" } }) { Text("Liga") }
        Button(attrs = { classes(*(listOfNotNull("small-btn", "small-btn-on".takeIf { subTab == "tournament" }).toTypedArray())); onClick { subTab = "tournament" } }) { Text("Turnaje") }
    }

    if (loading) {
        Div(attrs = { classes("card") }) { P { Text("Načítám…") } }
        return
    }
    error?.let { Div(attrs = { classes("card") }) { P { Text("Chyba: $it") } } }

    val activeChildren = children.filter { it.isActive }
    when (subTab) {
        "roster" -> ChildrenRosterAdmin(token, children, onChanged = { reloadKey++ })
        "training" -> ChildTrainingsSection(token, member, activeChildren)
        "round" -> ChildRoundsSection(token, member, activeChildren)
        else -> ChildTournamentsSection(token, member, activeChildren)
    }
}

// =================================================================================================
// Soupiska (sg_child) - obdoba ProfilesAdmin, ale s vlastním INSERTem (žádná externí soupiska).
// =================================================================================================

@Composable
private fun ChildrenRosterAdmin(token: String, children: List<SgChildDto>, onChanged: () -> Unit) {
    var adding by remember { mutableStateOf(false) }
    Div(attrs = { classes("card") }) {
        H3 { Text("Děti") }
        P(attrs = { classes("muted") }) { Text("Jméno, přezdívka, foto a viditelnost dítěte na veřejné stránce.") }
        if (children.isEmpty()) {
            P(attrs = { classes("muted") }) { Text("Zatím žádné děti.") }
        } else {
            Div(attrs = { classes("profile-admin-list") }) {
                children.forEach { c -> key(c.id) { ChildEditor(token, c, onChanged) } }
            }
        }
        if (adding) {
            key("new") { ChildEditor(token, null, onChanged = { adding = false; onChanged() }) }
        } else {
            Button(attrs = { classes("primary-btn"); onClick { adding = true } }) { Text("+ Přidat dítě") }
        }
    }
}

@Composable
private fun ChildEditor(token: String, existing: SgChildDto?, onChanged: () -> Unit) {
    var displayName by remember { mutableStateOf(existing?.displayName ?: "") }
    var nickname by remember { mutableStateOf(existing?.nickname ?: "") }
    var photoUrl by remember { mutableStateOf(existing?.photoUrl ?: "") }
    var bioHtml by remember { mutableStateOf(existing?.bioHtml ?: "") }
    var isActive by remember { mutableStateOf(existing?.isActive ?: true) }
    var sortOrder by remember { mutableStateOf(existing?.sortOrder ?: 0) }
    var busy by remember { mutableStateOf(false) }
    var uploading by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()
    val uploadKey = existing?.id ?: "new-${kotlin.js.Date().getTime()}"

    Div(attrs = { classes("profile-admin-item") }) {
        Div(attrs = { classes("profile-admin-photo") }) {
            if (photoUrl.isNotBlank()) {
                Img(src = photoUrl, alt = displayName, attrs = { classes("profile-thumb") })
            } else {
                Div(attrs = { classes("avatar-placeholder", "profile-thumb") }) { Text(displayName.take(1).ifBlank { "?" }) }
            }
            Label(attrs = { classes("small-btn", "upload-btn") }) {
                Text(if (uploading) "Nahrávám…" else "Foto")
                Input(type = InputType.File) {
                    attr("accept", "image/*")
                    style { property("display", "none") }
                    if (uploading) disabled()
                    onChange { ev ->
                        val input = ev.nativeEvent.target as? HTMLInputElement ?: return@onChange
                        val file = input.files?.item(0) ?: return@onChange
                        uploading = true; message = null
                        scope.launch {
                            try {
                                val bytes = readFileBytes(file)
                                val name = mediaFileName("child-$uploadKey", file)
                                photoUrl = Api.uploadMedia(token, name, file.type.ifBlank { "image/jpeg" }, bytes)
                            } catch (e: Throwable) {
                                message = "Upload selhal: ${e.apiMessage()}"
                            } finally { uploading = false }
                        }
                    }
                }
            }
            if (photoUrl.isNotBlank()) {
                Button(attrs = { classes("small-btn"); onClick { photoUrl = "" } }) { Text("Odebrat foto") }
            }
        }
        Div(attrs = { classes("profile-admin-fields") }) {
            Label { Text("Jméno"); Input(type = InputType.Text) { classes("field"); value(displayName); onInput { displayName = it.value } } }
            Label { Text("Přezdívka"); Input(type = InputType.Text) { classes("field"); placeholder("nepovinné"); value(nickname); onInput { nickname = it.value } } }
            Div(attrs = { classes("editor-label") }) { Text("Text (medailonek)") }
            key(existing?.id ?: "new") { ContentEditor(existing?.bioHtml ?: "", onHtmlChange = { bioHtml = it }) }
            Div(attrs = { classes("profile-admin-flags") }) {
                Label(attrs = { classes("cms-check") }) { CheckboxInput(checked = isActive) { onChange { isActive = it.value } }; Text(" Aktivní") }
                Label(attrs = { classes("cms-check") }) {
                    Text("Pořadí ")
                    Input(type = InputType.Number) { classes("field", "field-narrow"); value(sortOrder.toString()); onInput { sortOrder = it.value?.toInt() ?: 0 } }
                }
            }
            Div(attrs = { classes("form-actions") }) {
                Button(attrs = {
                    classes("primary-btn")
                    if (busy || uploading) disabled()
                    onClick {
                        if (displayName.isBlank()) { message = "Zadej jméno."; return@onClick }
                        busy = true; message = null
                        scope.launch {
                            try {
                                Api.upsertChild(token, existing?.id, displayName.trim(), nickname, photoUrl, bioHtml, isActive, sortOrder)
                                message = "Uloženo."
                                onChanged()
                            } catch (e: Throwable) {
                                message = "Uložení selhalo: ${e.apiMessage()}"
                            } finally { busy = false }
                        }
                    }
                }) { Text(if (busy) "Ukládám…" else "Uložit") }
                message?.let { Span(attrs = { classes("form-note") }) { Text(it) } }
            }
        }
    }
}

// =================================================================================================
// Tréninky dětí (soukromé) - mirror TrainingForm/TrainingsList (MembersScreen.kt), typováno na děti.
// =================================================================================================

@Composable
private fun ChildTrainingsSection(token: String, member: SgMemberDto?, children: List<SgChildDto>) {
    var trainings by remember { mutableStateOf<List<SgChildTrainingDto>>(emptyList()) }
    var scoresByTraining by remember { mutableStateOf<Map<String, List<SgChildScoreDto>>>(emptyMap()) }
    var sheetsByTraining by remember { mutableStateOf<Map<String, List<SgChildScoresheetDto>>>(emptyMap()) }
    var editing by remember { mutableStateOf<Triple<SgChildTrainingDto, List<SgChildScoreDto>, List<SgChildScoresheetDto>>?>(null) }
    var showForm by remember { mutableStateOf(false) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var reloadKey by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(reloadKey) {
        try {
            trainings = Api.fetchChildTrainings(token)
            val ids = trainings.map { it.id }
            scoresByTraining = Api.fetchChildTrainingScores(token, ids).groupBy { it.trainingId ?: "" }
            sheetsByTraining = Api.fetchChildTrainingSheets(token, ids).groupBy { it.trainingId ?: "" }
        } catch (e: Throwable) {
            error = e.apiMessage()
        } finally {
            loading = false
        }
    }

    if (loading) { Div(attrs = { classes("card") }) { P { Text("Načítám…") } }; return }
    error?.let { Div(attrs = { classes("card") }) { P { Text("Chyba: $it") } } }

    if (showForm || editing != null) {
        key(editing?.first?.id) {
            ChildTrainingForm(token, member, children, editing,
                onSaved = { editing = null; showForm = false; reloadKey++ },
                onCancel = { editing = null; showForm = false })
        }
    } else {
        Div(attrs = { classes("card") }) { Button(attrs = { classes("primary-btn"); onClick { showForm = true } }) { Text("+ Nový trénink") } }
    }

    ChildTrainingsList(
        trainings = trainings,
        scoresByTraining = scoresByTraining,
        children = children,
        onEdit = { t -> editing = Triple(t, scoresByTraining[t.id] ?: emptyList(), sheetsByTraining[t.id] ?: emptyList()) },
        onDelete = { t ->
            if (window.confirm("Smazat trénink ${t.date}?")) {
                scope.launch {
                    try {
                        Api.deleteChildTraining(token, t.id)
                        if (editing?.first?.id == t.id) editing = null
                        reloadKey++
                    } catch (e: Throwable) { error = "Smazání selhalo: ${e.apiMessage()}" }
                }
            }
        },
    )
}

@Composable
private fun ChildTrainingForm(
    token: String,
    member: SgMemberDto?,
    children: List<SgChildDto>,
    editing: Triple<SgChildTrainingDto, List<SgChildScoreDto>, List<SgChildScoresheetDto>>?,
    onSaved: () -> Unit,
    onCancel: () -> Unit,
) {
    val isEdit = editing != null
    var date by remember { mutableStateOf(editing?.first?.date ?: kotlin.js.Date().toISOString().substring(0, 10)) }
    var note by remember { mutableStateOf(editing?.first?.notes ?: "") }
    var games by remember { mutableStateOf(editing?.second?.maxOfOrNull { it.gameNo } ?: 3) }
    val scores = remember { mutableStateMapOf<String, String>().apply { editing?.second?.forEach { put("${it.childId}#${it.gameNo}", it.score.toString()) } } }
    val gameTypes = remember { mutableStateMapOf<String, String>().apply { editing?.second?.forEach { put("${it.childId}#${it.gameNo}", it.gameType) } } }
    val sheets = remember { mutableStateMapOf<String, List<FrameDto>>().apply { editing?.third?.forEach { put("${it.childId}#${it.gameNo}", it.frames) } } }
    var editorTarget by remember { mutableStateOf<EditorTarget?>(null) }
    var showTypes by remember { mutableStateOf(false) }
    var busy by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    Div(attrs = { classes("card") }) {
        H3 { Text(if (isEdit) "Úprava tréninku" else "Nový trénink") }
        Div(attrs = { classes("training-meta") }) {
            Label { Text("Datum"); Input(type = InputType.Date) { classes("field"); value(date); onInput { date = it.value } } }
            Label { Text("Poznámka"); Input(type = InputType.Text) { classes("field"); placeholder("nepovinné"); value(note); onInput { note = it.value } } }
            Div(attrs = { classes("game-controls") }) {
                Button(attrs = { classes("small-btn"); onClick { games++ } }) { Text("+ přidat hru") }
                Button(attrs = { classes("small-btn"); if (games <= 1) disabled(); onClick { if (games > 1) games-- } }) { Text("− odebrat hru") }
                Button(attrs = {
                    classes(*(listOfNotNull("small-btn", "icon-btn", "small-btn-on".takeIf { showTypes }).toTypedArray()))
                    attr("title", "Typy her"); attr("aria-label", "Typy her")
                    onClick { showTypes = !showTypes }
                }) { Text("⚙") }
            }
        }

        ChildScoreEntryTable(
            children = children, games = games, showTypes = showTypes, gameTypes = gameTypes,
            defaultType = "classic", scores = scores, sheets = sheets,
            onOpenEditor = { key, type -> editorTarget = EditorTarget(key, type) },
        )

        Div(attrs = { classes("form-actions") }) {
            Button(attrs = {
                classes("primary-btn")
                if (busy) disabled()
                onClick {
                    val createdBy = member?.userId ?: decodeJwtClaims(token)?.sub
                    if (createdBy == null) { message = "Chybí účet člena."; return@onClick }
                    val entries = children.flatMap { c ->
                        (1..games).mapNotNull { g ->
                            scores["${c.id}#$g"]?.trim()?.toIntOrNull()?.let { ChildScoreEntry(c.id, g, it, gameTypes["${c.id}#$g"] ?: "classic") }
                        }
                    }
                    if (entries.isEmpty()) { message = "Zadej aspoň jedno skóre."; return@onClick }
                    val sheetEntries = sheets.mapNotNull { (key, frames) ->
                        val parts = key.split("#")
                        val g = parts.getOrNull(1)?.toIntOrNull() ?: return@mapNotNull null
                        if (g > games) return@mapNotNull null
                        val type = gameTypes[key] ?: "classic"
                        ChildSheetEntry(parts[0], g, type, frames, Scoring.total(type, frames.map { it.t }))
                    }
                    busy = true; message = null
                    scope.launch {
                        try {
                            if (isEdit) {
                                val id = editing!!.first.id
                                Api.updateChildTraining(token, id, date, note)
                                Api.deleteChildTrainingScores(token, id); Api.addChildTrainingScores(token, id, entries)
                                Api.deleteChildTrainingSheets(token, id); Api.addChildTrainingSheets(token, id, createdBy, sheetEntries)
                            } else {
                                val id = Api.createChildTraining(token, createdBy, date, null, note)
                                Api.addChildTrainingScores(token, id, entries)
                                Api.addChildTrainingSheets(token, id, createdBy, sheetEntries)
                                scores.clear(); sheets.clear(); note = ""
                            }
                            onSaved()
                        } catch (e: Throwable) {
                            message = "Uložení selhalo: ${e.apiMessage()}"
                        } finally { busy = false }
                    }
                }
            }) { Text(if (busy) "Ukládám…" else if (isEdit) "Uložit změny" else "Uložit trénink") }
            Button(attrs = { classes("small-btn"); onClick { onCancel() } }) { Text("Zrušit") }
        }
        message?.let { Div(attrs = { classes("form-note") }) { Text(it) } }

        editorTarget?.let { target ->
            ScoresheetEditor(
                gameType = target.gameType,
                initialFrames = sheets[target.key] ?: emptyList(),
                onSave = { frames, total -> sheets[target.key] = frames; scores[target.key] = total.toString(); editorTarget = null },
                onClose = { editorTarget = null },
                onDelete = { sheets.remove(target.key); editorTarget = null },
            )
        }
    }
}

@Composable
private fun ChildTrainingsList(
    trainings: List<SgChildTrainingDto>,
    scoresByTraining: Map<String, List<SgChildScoreDto>>,
    children: List<SgChildDto>,
    onEdit: (SgChildTrainingDto) -> Unit,
    onDelete: (SgChildTrainingDto) -> Unit,
) {
    val nameById = remember(children) { children.associate { it.id to it.displayName } }
    Div(attrs = { classes("card") }) {
        H3 { Text("Odehrané tréninky") }
        if (trainings.isEmpty()) {
            P(attrs = { classes("muted") }) { Text("Zatím žádné tréninky.") }
            return@Div
        }
        Div(attrs = { classes("training-list") }) {
            trainings.forEach { t ->
                val scores = scoresByTraining[t.id] ?: emptyList()
                Div(attrs = { classes("training-item") }) {
                    Div(attrs = { classes("training-item-top") }) {
                        Div(attrs = { classes("training-head") }) {
                            Span(attrs = { classes("training-date") }) { Text(t.date) }
                            t.notes?.let { Span(attrs = { classes("muted") }) { Text(" · $it") } }
                            Span(attrs = { classes("muted") }) { Text(" · ${scores.size} skóre") }
                        }
                        Div(attrs = { classes("training-actions") }) {
                            Button(attrs = { classes("small-btn"); onClick { onEdit(t) } }) { Text("Upravit") }
                            Button(attrs = { classes("small-btn", "danger-btn"); onClick { onDelete(t) } }) { Text("Smazat") }
                        }
                    }
                    val byChild = scores.groupBy { it.childId }
                    Div(attrs = { classes("training-players") }) {
                        byChild.entries.sortedByDescending { it.value.map { s -> s.score }.average() }.forEach { (cid, sc) ->
                            val avg = (sc.map { it.score }.average() * 10).toInt() / 10.0
                            Span(attrs = { classes("training-player") }) { Text("${nameById[cid] ?: cid}: Ø $avg (${sc.size})") }
                        }
                    }
                }
            }
        }
    }
}

// =================================================================================================
// Kolo dětské ligy (veřejné, žádný soupeř - jen pořadí) - mirror TournamentForm/TournamentsList tvarem.
// =================================================================================================

private data class ChildRoundEdit(
    val round: SgChildRoundDto,
    val scores: List<SgChildScoreDto>,
    val sheets: List<SgChildScoresheetDto>,
    val placements: List<SgChildRoundPlacementDto>,
)

@Composable
private fun ChildRoundsSection(token: String, member: SgMemberDto?, children: List<SgChildDto>) {
    var rounds by remember { mutableStateOf<List<SgChildRoundDto>>(emptyList()) }
    var scoresByRound by remember { mutableStateOf<Map<String, List<SgChildScoreDto>>>(emptyMap()) }
    var sheetsByRound by remember { mutableStateOf<Map<String, List<SgChildScoresheetDto>>>(emptyMap()) }
    var placementsByRound by remember { mutableStateOf<Map<String, List<SgChildRoundPlacementDto>>>(emptyMap()) }
    var editing by remember { mutableStateOf<ChildRoundEdit?>(null) }
    var showForm by remember { mutableStateOf(false) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var reloadKey by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(reloadKey) {
        try {
            rounds = Api.fetchChildRounds(token)
            val ids = rounds.map { it.id }
            scoresByRound = Api.fetchChildRoundScores(token, ids).groupBy { it.roundId ?: "" }
            sheetsByRound = Api.fetchChildRoundSheets(token, ids).groupBy { it.roundId ?: "" }
            placementsByRound = Api.fetchChildRoundPlacements(token, ids).groupBy { it.roundId ?: "" }
        } catch (e: Throwable) {
            error = e.apiMessage()
        } finally {
            loading = false
        }
    }

    if (loading) { Div(attrs = { classes("card") }) { P { Text("Načítám…") } }; return }
    error?.let { Div(attrs = { classes("card") }) { P { Text("Chyba: $it") } } }

    if (showForm || editing != null) {
        key(editing?.round?.id) {
            ChildRoundForm(token, member, children, editing,
                onSaved = { editing = null; showForm = false; reloadKey++ },
                onCancel = { editing = null; showForm = false })
        }
    } else {
        Div(attrs = { classes("card") }) { Button(attrs = { classes("primary-btn"); onClick { showForm = true } }) { Text("+ Nové kolo") } }
    }

    ChildRoundsList(
        rounds = rounds,
        scoresByRound = scoresByRound,
        placementsByRound = placementsByRound,
        children = children,
        onEdit = { r -> editing = ChildRoundEdit(r, scoresByRound[r.id] ?: emptyList(), sheetsByRound[r.id] ?: emptyList(), placementsByRound[r.id] ?: emptyList()) },
        onDelete = { r ->
            if (window.confirm("Smazat kolo ${r.date}?")) {
                scope.launch {
                    try {
                        Api.deleteChildRound(token, r.id)
                        if (editing?.round?.id == r.id) editing = null
                        reloadKey++
                    } catch (e: Throwable) { error = "Smazání selhalo: ${e.apiMessage()}" }
                }
            }
        },
    )
}

@Composable
private fun ChildRoundForm(
    token: String,
    member: SgMemberDto?,
    children: List<SgChildDto>,
    editing: ChildRoundEdit?,
    onSaved: () -> Unit,
    onCancel: () -> Unit,
) {
    val isEdit = editing != null
    var date by remember { mutableStateOf(editing?.round?.date ?: kotlin.js.Date().toISOString().substring(0, 10)) }
    var roundNo by remember { mutableStateOf(editing?.round?.roundNo?.toString() ?: "") }
    var note by remember { mutableStateOf(editing?.round?.notes ?: "") }
    var games by remember { mutableStateOf(editing?.scores?.maxOfOrNull { it.gameNo } ?: 3) }
    val scores = remember { mutableStateMapOf<String, String>().apply { editing?.scores?.forEach { put("${it.childId}#${it.gameNo}", it.score.toString()) } } }
    val gameTypes = remember { mutableStateMapOf<String, String>().apply { editing?.scores?.forEach { put("${it.childId}#${it.gameNo}", it.gameType) } } }
    val sheets = remember { mutableStateMapOf<String, List<FrameDto>>().apply { editing?.sheets?.forEach { put("${it.childId}#${it.gameNo}", it.frames) } } }
    val placements = remember { mutableStateMapOf<String, String>().apply { editing?.placements?.forEach { put(it.childId, it.placement.toString()) } } }
    var showTypes by remember { mutableStateOf(false) }
    var editorTarget by remember { mutableStateOf<EditorTarget?>(null) }
    var busy by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    Div(attrs = { classes("card") }) {
        H3 { Text(if (isEdit) "Úprava kola" else "Nové kolo") }
        Div(attrs = { classes("training-meta") }) {
            Label { Text("Datum"); Input(type = InputType.Date) { classes("field"); value(date); onInput { date = it.value } } }
            Label { Text("Číslo kola"); Input(type = InputType.Text) { classes("field", "field-narrow"); attr("inputmode", "numeric"); placeholder("nepovinné"); value(roundNo); onInput { roundNo = it.value } } }
            Label { Text("Poznámka"); Input(type = InputType.Text) { classes("field"); placeholder("nepovinné"); value(note); onInput { note = it.value } } }
            Div(attrs = { classes("game-controls") }) {
                Button(attrs = { classes("small-btn"); onClick { games++ } }) { Text("+ přidat hru") }
                Button(attrs = { classes("small-btn"); if (games <= 1) disabled(); onClick { if (games > 1) games-- } }) { Text("− odebrat hru") }
                Button(attrs = {
                    classes(*(listOfNotNull("small-btn", "icon-btn", "small-btn-on".takeIf { showTypes }).toTypedArray()))
                    attr("title", "Typy her"); attr("aria-label", "Typy her")
                    onClick { showTypes = !showTypes }
                }) { Text("⚙") }
            }
        }

        ChildScoreEntryTable(
            children = children, games = games, showTypes = showTypes, gameTypes = gameTypes,
            defaultType = "classic", scores = scores, sheets = sheets,
            onOpenEditor = { key, type -> editorTarget = EditorTarget(key, type) },
            headExtra = { Text("Pořadí") },
            rowExtra = { c ->
                Input(type = InputType.Text) {
                    classes("placement-input"); attr("inputmode", "numeric"); placeholder("–")
                    value(placements[c.id] ?: "")
                    onInput { placements[c.id] = it.value }
                }
            },
        )

        Div(attrs = { classes("form-actions") }) {
            Button(attrs = {
                classes("primary-btn")
                if (busy) disabled()
                onClick {
                    val createdBy = member?.userId ?: decodeJwtClaims(token)?.sub
                    if (createdBy == null) { message = "Chybí účet člena."; return@onClick }
                    val entries = children.flatMap { c ->
                        (1..games).mapNotNull { g ->
                            scores["${c.id}#$g"]?.trim()?.toIntOrNull()?.let { ChildScoreEntry(c.id, g, it, gameTypes["${c.id}#$g"] ?: "classic") }
                        }
                    }
                    if (entries.isEmpty()) { message = "Zadej aspoň jedno skóre."; return@onClick }
                    val sheetEntries = sheets.mapNotNull { (key, frames) ->
                        val parts = key.split("#")
                        val g = parts.getOrNull(1)?.toIntOrNull() ?: return@mapNotNull null
                        if (g > games) return@mapNotNull null
                        val type = gameTypes[key] ?: "classic"
                        ChildSheetEntry(parts[0], g, type, frames, Scoring.total(type, frames.map { it.t }))
                    }
                    val placementEntries = placements.mapNotNull { (cid, v) -> v.trim().toIntOrNull()?.takeIf { it >= 1 }?.let { ChildPlacementEntry(cid, it) } }
                    busy = true; message = null
                    scope.launch {
                        try {
                            val roundNoInt = roundNo.trim().toIntOrNull()
                            if (isEdit) {
                                val id = editing!!.round.id
                                Api.updateChildRound(token, id, date, roundNoInt, note)
                                Api.deleteChildRoundScores(token, id); Api.addChildRoundScores(token, id, entries)
                                Api.deleteChildRoundSheets(token, id); Api.addChildRoundSheets(token, id, createdBy, sheetEntries)
                                Api.deleteChildRoundPlacements(token, id); Api.addChildRoundPlacements(token, id, placementEntries)
                            } else {
                                val id = Api.createChildRound(token, createdBy, date, roundNoInt, null, note)
                                Api.addChildRoundScores(token, id, entries)
                                Api.addChildRoundSheets(token, id, createdBy, sheetEntries)
                                Api.addChildRoundPlacements(token, id, placementEntries)
                                scores.clear(); sheets.clear(); placements.clear(); roundNo = ""; note = ""
                            }
                            onSaved()
                        } catch (e: Throwable) {
                            message = "Uložení selhalo: ${e.apiMessage()}"
                        } finally { busy = false }
                    }
                }
            }) { Text(if (busy) "Ukládám…" else if (isEdit) "Uložit změny" else "Uložit kolo") }
            Button(attrs = { classes("small-btn"); onClick { onCancel() } }) { Text("Zrušit") }
        }
        message?.let { Div(attrs = { classes("form-note") }) { Text(it) } }

        editorTarget?.let { target ->
            ScoresheetEditor(
                gameType = target.gameType,
                initialFrames = sheets[target.key] ?: emptyList(),
                onSave = { frames, total -> sheets[target.key] = frames; scores[target.key] = total.toString(); editorTarget = null },
                onClose = { editorTarget = null },
                onDelete = { sheets.remove(target.key); editorTarget = null },
            )
        }
    }
}

@Composable
private fun ChildRoundsList(
    rounds: List<SgChildRoundDto>,
    scoresByRound: Map<String, List<SgChildScoreDto>>,
    placementsByRound: Map<String, List<SgChildRoundPlacementDto>>,
    children: List<SgChildDto>,
    onEdit: (SgChildRoundDto) -> Unit,
    onDelete: (SgChildRoundDto) -> Unit,
) {
    val nameById = remember(children) { children.associate { it.id to it.displayName } }
    Div(attrs = { classes("card") }) {
        H3 { Text("Odehraná kola") }
        if (rounds.isEmpty()) {
            P(attrs = { classes("muted") }) { Text("Zatím žádná kola.") }
            return@Div
        }
        Div(attrs = { classes("training-list") }) {
            rounds.forEach { r ->
                val scores = scoresByRound[r.id] ?: emptyList()
                val placements = (placementsByRound[r.id] ?: emptyList()).sortedBy { it.placement }
                Div(attrs = { classes("training-item") }) {
                    Div(attrs = { classes("training-item-top") }) {
                        Div(attrs = { classes("training-head") }) {
                            Span(attrs = { classes("training-date") }) { Text(r.date) }
                            r.roundNo?.let { Span(attrs = { classes("muted") }) { Text(" · ${it}. kolo") } }
                            r.notes?.let { Span(attrs = { classes("muted") }) { Text(" · $it") } }
                        }
                        Div(attrs = { classes("training-actions") }) {
                            Button(attrs = { classes("small-btn"); onClick { onEdit(r) } }) { Text("Upravit") }
                            Button(attrs = { classes("small-btn", "danger-btn"); onClick { onDelete(r) } }) { Text("Smazat") }
                        }
                    }
                    if (placements.isNotEmpty()) {
                        Div(attrs = { classes("training-players") }) {
                            placements.forEach { pl -> Span(attrs = { classes("placement-chip") }) { Text("${pl.placement}. ${nameById[pl.childId] ?: pl.childId}") } }
                        }
                    }
                    val byChild = scores.groupBy { it.childId }
                    Div(attrs = { classes("training-players") }) {
                        byChild.entries.sortedByDescending { it.value.map { s -> s.score }.average() }.forEach { (cid, sc) ->
                            val avg = (sc.map { it.score }.average() * 10).toInt() / 10.0
                            Span(attrs = { classes("training-player") }) { Text("${nameById[cid] ?: cid}: Ø $avg (${sc.size})") }
                        }
                    }
                }
            }
        }
    }
}

// =================================================================================================
// Speciální turnaje (veřejné) - jako kolo, ale s názvem a parťák/mód poznámkou navíc.
// =================================================================================================

private data class ChildTournamentEdit(
    val tournament: SgChildTournamentDto,
    val scores: List<SgChildScoreDto>,
    val sheets: List<SgChildScoresheetDto>,
    val entries: List<SgChildTournamentEntryDto>,
)

@Composable
private fun ChildTournamentsSection(token: String, member: SgMemberDto?, children: List<SgChildDto>) {
    var tournaments by remember { mutableStateOf<List<SgChildTournamentDto>>(emptyList()) }
    var scoresByTournament by remember { mutableStateOf<Map<String, List<SgChildScoreDto>>>(emptyMap()) }
    var sheetsByTournament by remember { mutableStateOf<Map<String, List<SgChildScoresheetDto>>>(emptyMap()) }
    var entriesByTournament by remember { mutableStateOf<Map<String, List<SgChildTournamentEntryDto>>>(emptyMap()) }
    var editing by remember { mutableStateOf<ChildTournamentEdit?>(null) }
    var showForm by remember { mutableStateOf(false) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var reloadKey by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(reloadKey) {
        try {
            tournaments = Api.fetchChildTournaments(token)
            val ids = tournaments.map { it.id }
            scoresByTournament = Api.fetchChildTournamentScores(token, ids).groupBy { it.tournamentId ?: "" }
            sheetsByTournament = Api.fetchChildTournamentSheets(token, ids).groupBy { it.tournamentId ?: "" }
            entriesByTournament = Api.fetchChildTournamentEntries(token, ids).groupBy { it.tournamentId ?: "" }
        } catch (e: Throwable) {
            error = e.apiMessage()
        } finally {
            loading = false
        }
    }

    if (loading) { Div(attrs = { classes("card") }) { P { Text("Načítám…") } }; return }
    error?.let { Div(attrs = { classes("card") }) { P { Text("Chyba: $it") } } }

    if (showForm || editing != null) {
        key(editing?.tournament?.id) {
            ChildTournamentForm(token, member, children, editing,
                onSaved = { editing = null; showForm = false; reloadKey++ },
                onCancel = { editing = null; showForm = false })
        }
    } else {
        Div(attrs = { classes("card") }) { Button(attrs = { classes("primary-btn"); onClick { showForm = true } }) { Text("+ Nový turnaj") } }
    }

    ChildTournamentsList(
        tournaments = tournaments,
        scoresByTournament = scoresByTournament,
        entriesByTournament = entriesByTournament,
        children = children,
        onEdit = { t -> editing = ChildTournamentEdit(t, scoresByTournament[t.id] ?: emptyList(), sheetsByTournament[t.id] ?: emptyList(), entriesByTournament[t.id] ?: emptyList()) },
        onDelete = { t ->
            if (window.confirm("Smazat turnaj ${t.name}?")) {
                scope.launch {
                    try {
                        Api.deleteChildTournament(token, t.id)
                        if (editing?.tournament?.id == t.id) editing = null
                        reloadKey++
                    } catch (e: Throwable) { error = "Smazání selhalo: ${e.apiMessage()}" }
                }
            }
        },
    )
}

@Composable
private fun ChildTournamentForm(
    token: String,
    member: SgMemberDto?,
    children: List<SgChildDto>,
    editing: ChildTournamentEdit?,
    onSaved: () -> Unit,
    onCancel: () -> Unit,
) {
    val isEdit = editing != null
    var name by remember { mutableStateOf(editing?.tournament?.name ?: "") }
    var date by remember { mutableStateOf(editing?.tournament?.date ?: kotlin.js.Date().toISOString().substring(0, 10)) }
    var note by remember { mutableStateOf(editing?.tournament?.notes ?: "") }
    var tourType by remember { mutableStateOf(editing?.tournament?.gameType ?: "classic") }
    var games by remember { mutableStateOf(editing?.scores?.maxOfOrNull { it.gameNo } ?: 3) }
    val scores = remember { mutableStateMapOf<String, String>().apply { editing?.scores?.forEach { put("${it.childId}#${it.gameNo}", it.score.toString()) } } }
    val gameTypes = remember { mutableStateMapOf<String, String>().apply { editing?.scores?.forEach { put("${it.childId}#${it.gameNo}", it.gameType) } } }
    val sheets = remember { mutableStateMapOf<String, List<FrameDto>>().apply { editing?.sheets?.forEach { put("${it.childId}#${it.gameNo}", it.frames) } } }
    val placements = remember { mutableStateMapOf<String, String>().apply { editing?.entries?.forEach { e -> e.placement?.let { put(e.childId, it.toString()) } } } }
    val partnerNotes = remember { mutableStateMapOf<String, String>().apply { editing?.entries?.forEach { e -> e.partnerNote?.let { put(e.childId, it) } } } }
    var showTypes by remember { mutableStateOf(false) }
    var editorTarget by remember { mutableStateOf<EditorTarget?>(null) }
    var busy by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    Div(attrs = { classes("card") }) {
        H3 { Text(if (isEdit) "Úprava turnaje" else "Nový turnaj") }
        Div(attrs = { classes("training-meta") }) {
            Label { Text("Název"); Input(type = InputType.Text) { classes("field"); placeholder("název turnaje"); value(name); onInput { name = it.value } } }
            Label { Text("Datum"); Input(type = InputType.Date) { classes("field"); value(date); onInput { date = it.value } } }
            Label {
                Text("Typ")
                Select(attrs = { classes("field"); onChange { ev -> ev.value?.let { tourType = it } } }) {
                    CHILD_BOWLING_TYPES.forEach { (code, label) -> Option(code, attrs = { if (tourType == code) attr("selected", "selected") }) { Text(label) } }
                }
            }
            Label { Text("Poznámka"); Input(type = InputType.Text) { classes("field"); placeholder("nepovinné"); value(note); onInput { note = it.value } } }
            Div(attrs = { classes("game-controls") }) {
                Button(attrs = { classes("small-btn"); onClick { games++ } }) { Text("+ přidat hru") }
                Button(attrs = { classes("small-btn"); if (games <= 1) disabled(); onClick { if (games > 1) games-- } }) { Text("− odebrat hru") }
                Button(attrs = {
                    classes(*(listOfNotNull("small-btn", "icon-btn", "small-btn-on".takeIf { showTypes }).toTypedArray()))
                    attr("title", "Typy her"); attr("aria-label", "Typy her")
                    onClick { showTypes = !showTypes }
                }) { Text("⚙") }
            }
        }

        ChildScoreEntryTable(
            children = children, games = games, showTypes = showTypes, gameTypes = gameTypes,
            defaultType = tourType, scores = scores, sheets = sheets,
            onOpenEditor = { key, type -> editorTarget = EditorTarget(key, type) },
            headExtra = { Text("Pořadí / Parťák") },
            rowExtra = { c ->
                Div(attrs = { classes("placement-input-group") }) {
                    Input(type = InputType.Text) {
                        classes("placement-input"); attr("inputmode", "numeric"); placeholder("pořadí")
                        value(placements[c.id] ?: "")
                        onInput { placements[c.id] = it.value }
                    }
                    Input(type = InputType.Text) {
                        classes("field"); placeholder("parťák/mód")
                        value(partnerNotes[c.id] ?: "")
                        onInput { partnerNotes[c.id] = it.value }
                    }
                }
            },
        )

        Div(attrs = { classes("form-actions") }) {
            Button(attrs = {
                classes("primary-btn")
                if (busy) disabled()
                onClick {
                    val createdBy = member?.userId ?: decodeJwtClaims(token)?.sub
                    if (createdBy == null) { message = "Chybí účet člena."; return@onClick }
                    if (name.isBlank()) { message = "Zadej název turnaje."; return@onClick }
                    val entries = children.flatMap { c ->
                        (1..games).mapNotNull { g ->
                            scores["${c.id}#$g"]?.trim()?.toIntOrNull()?.let { ChildScoreEntry(c.id, g, it, gameTypes["${c.id}#$g"] ?: tourType) }
                        }
                    }
                    if (entries.isEmpty()) { message = "Zadej aspoň jedno skóre."; return@onClick }
                    val sheetEntries = sheets.mapNotNull { (key, frames) ->
                        val parts = key.split("#")
                        val g = parts.getOrNull(1)?.toIntOrNull() ?: return@mapNotNull null
                        if (g > games) return@mapNotNull null
                        val type = gameTypes[key] ?: tourType
                        ChildSheetEntry(parts[0], g, type, frames, Scoring.total(type, frames.map { it.t }))
                    }
                    val tourEntries = children.mapNotNull { c ->
                        val placement = placements[c.id]?.trim()?.toIntOrNull()?.takeIf { it >= 1 }
                        val partnerNote = partnerNotes[c.id]?.trim()?.ifBlank { null }
                        if (placement == null && partnerNote == null) null else ChildTournamentEntryEntry(c.id, placement, partnerNote)
                    }
                    busy = true; message = null
                    scope.launch {
                        try {
                            if (isEdit) {
                                val id = editing!!.tournament.id
                                Api.updateChildTournament(token, id, name, date, tourType, note)
                                Api.deleteChildTournamentScores(token, id); Api.addChildTournamentScores(token, id, entries)
                                Api.deleteChildTournamentSheets(token, id); Api.addChildTournamentSheets(token, id, createdBy, sheetEntries)
                                Api.deleteChildTournamentEntries(token, id); Api.addChildTournamentEntries(token, id, tourEntries)
                            } else {
                                val id = Api.createChildTournament(token, createdBy, name, date, tourType, note)
                                Api.addChildTournamentScores(token, id, entries)
                                Api.addChildTournamentSheets(token, id, createdBy, sheetEntries)
                                Api.addChildTournamentEntries(token, id, tourEntries)
                                scores.clear(); sheets.clear(); placements.clear(); partnerNotes.clear(); name = ""; note = ""
                            }
                            onSaved()
                        } catch (e: Throwable) {
                            message = "Uložení selhalo: ${e.apiMessage()}"
                        } finally { busy = false }
                    }
                }
            }) { Text(if (busy) "Ukládám…" else if (isEdit) "Uložit změny" else "Uložit turnaj") }
            Button(attrs = { classes("small-btn"); onClick { onCancel() } }) { Text("Zrušit") }
        }
        message?.let { Div(attrs = { classes("form-note") }) { Text(it) } }

        editorTarget?.let { target ->
            ScoresheetEditor(
                gameType = target.gameType,
                initialFrames = sheets[target.key] ?: emptyList(),
                onSave = { frames, total -> sheets[target.key] = frames; scores[target.key] = total.toString(); editorTarget = null },
                onClose = { editorTarget = null },
                onDelete = { sheets.remove(target.key); editorTarget = null },
            )
        }
    }
}

@Composable
private fun ChildTournamentsList(
    tournaments: List<SgChildTournamentDto>,
    scoresByTournament: Map<String, List<SgChildScoreDto>>,
    entriesByTournament: Map<String, List<SgChildTournamentEntryDto>>,
    children: List<SgChildDto>,
    onEdit: (SgChildTournamentDto) -> Unit,
    onDelete: (SgChildTournamentDto) -> Unit,
) {
    val nameById = remember(children) { children.associate { it.id to it.displayName } }
    Div(attrs = { classes("card") }) {
        H3 { Text("Odehrané turnaje") }
        if (tournaments.isEmpty()) {
            P(attrs = { classes("muted") }) { Text("Zatím žádné turnaje.") }
            return@Div
        }
        Div(attrs = { classes("training-list") }) {
            tournaments.forEach { t ->
                val scores = scoresByTournament[t.id] ?: emptyList()
                val entries = (entriesByTournament[t.id] ?: emptyList()).sortedBy { it.placement ?: Int.MAX_VALUE }
                Div(attrs = { classes("training-item") }) {
                    Div(attrs = { classes("training-item-top") }) {
                        Div(attrs = { classes("training-head") }) {
                            Span(attrs = { classes("training-date") }) { Text(t.name) }
                            Span(attrs = { classes("muted") }) { Text(" · ${t.date} · ${childBowlingTypeLabel(t.gameType)}") }
                            t.notes?.let { Span(attrs = { classes("muted") }) { Text(" · $it") } }
                        }
                        Div(attrs = { classes("training-actions") }) {
                            Button(attrs = { classes("small-btn"); onClick { onEdit(t) } }) { Text("Upravit") }
                            Button(attrs = { classes("small-btn", "danger-btn"); onClick { onDelete(t) } }) { Text("Smazat") }
                        }
                    }
                    if (entries.isNotEmpty()) {
                        Div(attrs = { classes("training-players") }) {
                            entries.forEach { e ->
                                val label = buildString {
                                    e.placement?.let { append("$it. ") }
                                    append(nameById[e.childId] ?: e.childId)
                                    e.partnerNote?.takeIf { it.isNotBlank() }?.let { append(" ($it)") }
                                }
                                Span(attrs = { classes("placement-chip") }) { Text(label) }
                            }
                        }
                    }
                    val byChild = scores.groupBy { it.childId }
                    Div(attrs = { classes("training-players") }) {
                        byChild.entries.sortedByDescending { it.value.map { s -> s.score }.average() }.forEach { (cid, sc) ->
                            val avg = (sc.map { it.score }.average() * 10).toInt() / 10.0
                            Span(attrs = { classes("training-player") }) { Text("${nameById[cid] ?: cid}: Ø $avg (${sc.size})") }
                        }
                    }
                }
            }
        }
    }
}
